import React, { FC, useState, useEffect, useCallback } from 'react';
import { Player, PlayerEvent, TimeChangedEvent, SeekEvent, TimeMode, PlayerConfig, PlayerAPI } from 'bitmovin-player';
import { UIFactory } from 'bitmovin-player-ui';
import { Button, Grid, Typography, makeStyles } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import muxjs from 'mux.js';

import awsConfig from '../../aws-exports';
import { Period } from '../../utils/types';
import SimpleTextInfo from './SimpleTextInfo';
import ClipperTime from './ClipperTime';

dayjs.extend(localizedFormat);
dayjs.extend(duration);

declare global {
	interface Window {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		muxjs: any;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		player: any;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		playerUI: any;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		playerAdditional: any;
	}
}

const useStyles = makeStyles((theme) => ({
	duration: {
		textAlign: 'center',
		marginTop: theme.spacing(1),
		width: '100%'
	},
	button: {
		margin: theme.spacing(0.5)
	}
}));

const Clipper: FC<ClipperProps> = (props) => {
	const { url, additionalChannels, start, duration, channelId, onWatchClip, onAddClip } = props;

	const classes = useStyles();

	const [position, setPosition] = useState<number>(0);
	const [cutEnd, setCutEnd] = useState<string | null>();
	const [cutStart, setCutStart] = useState<string | null>();
	const [playing, setPlaying] = useState<boolean>(false);

	window.muxjs = muxjs;

	const setPlayingWithAdditional = (playingValue: boolean): void => {
		if (window.playerAdditional) {
			if (playingValue) {
				window.playerAdditional.forEach((player: PlayerAPI) => player.play());
			} else {
				window.playerAdditional.forEach((player: PlayerAPI) => player.pause());
			}
		}
		setPlaying(playingValue);
	};

	const setPositionFromEvent = (time: number): void => {
		setPosition(time);
	};

	const setupPlayer = useCallback(() => {
		setPlayingWithAdditional(false);
		const bitmovinConfig = (height: string, width: string): PlayerConfig => ({
			key: awsConfig.bitmovinLicenseKey,
			playback: {
				muted: true
			},
			style: {
				width,
				aspectratio: '16:9',
				height
			}
		});

		const htmlVideo = document.getElementById('video')!;
		const player = new Player(htmlVideo, bitmovinConfig('400px', '100%'));
		window.player = player;
		window.playerUI = UIFactory.buildDefaultUI(player);
		window.playerAdditional = [];

		additionalChannels?.forEach((_additionalChannel, idx): void => {
			const htmlVideoAdditional = document.getElementById(`videoAdditional${idx}`)!;
			const playerAdditional = new Player(htmlVideoAdditional, bitmovinConfig('200px', '50%'));
			window.playerAdditional.push(playerAdditional);
		});

		const playingEvent = (): void => {
			setPlayingWithAdditional(true);
		};
		player.on(PlayerEvent.Playing, playingEvent);

		const pausedEvent = (): void => {
			setPlayingWithAdditional(false);
		};
		player.on(PlayerEvent.Paused, pausedEvent);

		const timeChangedEvent = (e: unknown): void => {
			const event = e as TimeChangedEvent;
			if (player.isLive()) {
				const relativeTime = player.getCurrentTime(TimeMode.RelativeTime);
				setPositionFromEvent(relativeTime);
				shiftAdditionalPlayer();
			} else {
				setPositionFromEvent(event.time);
			}
		};
		player.on(PlayerEvent.TimeChanged, timeChangedEvent);

		const seekEvent = (e: unknown): void => {
			//Seeking only works for non Live streams
			const event = e as SeekEvent;
			window.playerAdditional.forEach((player: PlayerAPI) => player.seek(event.seekTarget));
			setPositionFromEvent(event.seekTarget);
		};
		player.on(PlayerEvent.Seek, seekEvent);

		return (): void => {
			player.off(PlayerEvent.Playing, playingEvent);
			player.off(PlayerEvent.Paused, pausedEvent);
			player.off(PlayerEvent.TimeChanged, timeChangedEvent);
			player.off(PlayerEvent.Seek, seekEvent);
		};
	}, [additionalChannels]);

	const shiftAdditionalPlayer = (): void => {
		if (window.playerAdditional && window.playerAdditional.length > 0) {
			const mainPlayerTimeShift = window.player.getTimeShift();
			const additionalPlayerTimeShift = window.playerAdditional[0].getTimeShift();
			if (mainPlayerTimeShift - additionalPlayerTimeShift < -2 || mainPlayerTimeShift - additionalPlayerTimeShift > 2) {
				window.playerAdditional.forEach((player: PlayerAPI) => player.timeShift(mainPlayerTimeShift));
			}
		}
	};

	const loadNewPlayerTime = useCallback(
		async (url: string): Promise<void> => {
			setPlayingWithAdditional(false);
			window.player
				.load({
					hls: url
				})
				.then(() => {
					window.player.play();
				});

			additionalChannels?.forEach((additionalChannel, idx): void => {
				const player = window.playerAdditional[idx];
				if (player && additionalChannel) {
					player
						.load({
							hls: additionalChannel.url
						})
						.then(() => {
							player.play();
						});
				}
			});
		},
		[additionalChannels]
	);

	useEffect(() => {
		setupPlayer();
	}, [setupPlayer]);

	useEffect(() => {
		if (url) {
			loadNewPlayerTime(url);
		}
	}, [url, duration, start, loadNewPlayerTime]);

	useEffect(() => {
		if (window.playerUI) {
			const uiMarkers = [...window.playerUI.getTimelineMarkers()];
			if (uiMarkers.length > 0) {
				uiMarkers.forEach((marker: Marker) => {
					window.playerUI.removeTimelineMarker(marker);
				});
			}

			const newMarkers = [];
			if (cutStart) {
				newMarkers.push(getMarker(start, cutStart, 'Clip Start'));
			}

			if (cutEnd) {
				newMarkers.push(getMarker(start, cutEnd, 'Clip End'));
			}

			newMarkers.forEach((marker) => {
				window.playerUI.addTimelineMarker(marker);
			});
		}
	}, [duration, start, cutStart, cutEnd, playing]);

	const getMarker = (startPosition: string, endPosition: string, title: string): { time: number; title: string } => {
		if (window.player.isLive()) {
			const positionToMoveTo =
				window.player.getCurrentTime() -
				window.player.getCurrentTime('relativetime') +
				dayjs.duration(dayjs(endPosition).diff(dayjs(startPosition))).asSeconds();
			return { time: positionToMoveTo, title };
		}
		const clipDurationFromVideoStart = dayjs.duration(dayjs(endPosition).diff(dayjs(startPosition)));
		return {
			time: clipDurationFromVideoStart.asMilliseconds() / 1000,
			title
		};
	};

	const onSeek = (seekingPostion: string, pause?: boolean): void => {
		if (pause) {
			window.player.pause();
			window.playerAdditional.forEach((player: PlayerAPI) => player.pause());
		}
		const seekDuration = dayjs.duration(dayjs(seekingPostion).diff(dayjs(start))).asMilliseconds() / 1000;
		if (seekDuration < 0) {
			onWatchClip(dayjs(start).add(-10, 'second').toISOString(), duration + 10, channelId);
		} else if (seekDuration > duration) {
			onWatchClip(start, duration + 10, channelId);
		}
		if (window.player.isLive()) {
			const TIME_SHIFT_GAP = 41; //magic number, why is this required TODO?
			const timeShiftValue = window.player.getCurrentTime() - window.player.getCurrentTime('relativetime') + seekDuration + TIME_SHIFT_GAP;
			window.player.timeShift(timeShiftValue);
			window.playerAdditional.forEach((player: PlayerAPI) => player.timeShift(timeShiftValue));
		} else {
			window.player.seek(seekDuration);
			window.playerAdditional.forEach((player: PlayerAPI) => player.seek(seekDuration));
		}
	};

	const removeWaterMarkStyles = '.bmpui-ui-watermark {display: none!important;}';

	//prettier-ignore
	const selectedCutDuration =
        cutEnd && cutStart
            ? dayjs
                .duration(dayjs(cutEnd).diff(dayjs(cutStart)))
                .asSeconds()
                .toString()
            : 0;

	const addButtonAction = (): void => {
		if (areTimesValid()) {
			if (
				selectedCutDuration > 300 &&
				!window.confirm(
					'The duration is more than 5 minutes, which is quite long for a single highlight clip. Are you sure you are happy with this clip?'
				)
			) {
				return;
			}
			setCutStart(null);
			setCutEnd(null);
			onAddClip({
				source: {
					channelId: channelId,
					type: 'live',
					startTime: cutStart,
					endTime: cutEnd
				},
				startGameTime: {
					position: '0',
					segment: 1
				},
				type: 'Action'
			});
		}
	};

	const areTimesValid = (): boolean => {
		if (
			!cutStart ||
			!cutEnd ||
			cutStart >= cutEnd ||
			selectedCutDuration < 1 ||
			dayjs(Date.now()).isBefore(dayjs(cutStart)) ||
			dayjs(Date.now()).isBefore(dayjs(cutEnd)) ||
			dayjs(dayjs(cutEnd)).isBefore(dayjs(cutStart))
		) {
			return false;
		}

		return true;
	};

	const timesValid = areTimesValid();
	let endBeforeStart = false;
	if (cutStart && cutEnd && dayjs(dayjs(cutEnd)).isBefore(dayjs(cutStart))) {
		endBeforeStart = true;
	}

	return url ? (
		<>
			{/*TODO can this be added in a better way? */}
			<style>{removeWaterMarkStyles}</style>
			<Grid container spacing={1}>
				<Grid item xs={12} md={6}>
					<SimpleTextInfo label='Channel' value={channelId} />
					<div id='video' />
					<SimpleTextInfo label='URL' value={url || ''} />
					<SimpleTextInfo label='Position' value={position || ''} />

					{additionalChannels &&
						additionalChannels.map((additionalChannel, idx) => {
							return (
								<div key={`videoAdditional${idx}`} style={{ marginTop: 15 }}>
									<SimpleTextInfo label='Related Channel' value={additionalChannel.channel} />
									<div id={`videoAdditional${idx}`} />
								</div>
							);
						})}
				</Grid>
				<Grid container justify='center' item xs={12} md={6}>
					<ClipperTime title='Cut Start' cutPosition={cutStart} start={start} currentTime={position} setCutPosition={setCutStart} seek={onSeek} />
					<ClipperTime
						title='Cut End'
						cutPosition={cutEnd}
						start={start}
						currentTime={position}
						setCutPosition={setCutEnd}
						seek={onSeek}
						warning={endBeforeStart ? 'Cut end cannot be before the cut start' : null}
					/>
					<Button
						onClick={(): void => addButtonAction()}
						variant='contained'
						color='primary'
						className={classes.button}
						disabled={!timesValid}
						startIcon={<AddIcon />}>
						Add
					</Button>
					<Button
						onClick={(): void => {
							if (cutStart && cutEnd) {
								onWatchClip(cutStart, dayjs.duration(dayjs(cutEnd).diff(dayjs(cutStart))).asMilliseconds() / 1000, channelId);
								window.player.pause(); // pauses player to prevent continuation of audio
								window.player.seek(0);
								window.playerAdditional.forEach((player: PlayerAPI) => player.seek(0));
							}
						}}
						variant='contained'
						color='secondary'
						className={classes.button}
						disabled={!timesValid}
						startIcon={<PlayArrowIcon />}>
						Play
					</Button>
					<p className={classes.duration}>Clip Duration: {selectedCutDuration}s</p>
				</Grid>
			</Grid>
		</>
	) : (
		<Typography align='center'>No video asset found</Typography>
	);
};

type Marker = {
	time: number;
	title: string;
};

type ClipperProps = {
	url: string;
	additionalChannels: AdditionalChannel[] | null;
	start: string;
	duration: number;
	channelId: string;
	onWatchClip: (startTime: string, duration: number, channelId: string) => void;
	onAddClip: (clip: Period) => void;
};

export type AdditionalChannel = {
	channel: string;
	url: string;
};

export default Clipper;
